import React from 'react'

import { HStack, Tooltip, T, Icon, VStack } from '../../govwell-ui'

const hoverFocusRingClassName =
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-700'

type LanguageSupportEntry = {
  ariaLabel: string
  display: string
  id: string
  role: React.AriaRole
  tooltipLines: readonly string[]
  variant: 'flag' | 'badge'
}

const languageSupportEntries: readonly LanguageSupportEntry[] = [
  {
    id: 'spanish',
    display: '🇪🇸',
    ariaLabel: 'Community Assistant available in Spanish',
    role: 'img',
    tooltipLines: [
      'The Community Assistant is available in Spanish.',
      'El Asistente Comunitario está disponible en español.',
    ],
    variant: 'flag',
  },
  {
    id: 'chinese',
    display: '🇨🇳',
    ariaLabel: '社区助手支持中文',
    role: 'img',
    tooltipLines: ['The Community Assistant is available in Chinese.', '社区助手支持中文。'],
    variant: 'flag',
  },
  {
    id: 'vietnamese',
    display: '🇻🇳',
    ariaLabel: 'Community Assistant available in Vietnamese',
    role: 'img',
    tooltipLines: [
      'The Community Assistant is available in Vietnamese.',
      'Trợ lý Cộng đồng hiện có bằng tiếng Việt.',
    ],
    variant: 'flag',
  },
  {
    id: 'french',
    display: '🇫🇷',
    ariaLabel: 'Assistant communautaire disponible en français',
    role: 'img',
    tooltipLines: [
      'The Community Assistant is available in French.',
      'L’assistant communautaire est disponible en français.',
    ],
    variant: 'flag',
  },
  {
    id: 'arabic',
    display: '🇸🇦',
    ariaLabel: 'يتوفر مساعد المجتمع باللغة العربية',
    role: 'img',
    tooltipLines: [
      'The Community Assistant is available in Arabic.',
      'يتوفر مساعد المجتمع باللغة العربية.',
    ],
    variant: 'flag',
  },
  {
    id: 'summary',
    display: '+80',
    ariaLabel: 'The Community Assistant is available in over eighty languages',
    role: 'status',
    tooltipLines: ['The Community Assistant is available in over 80 languages. Give it a try!'],
    variant: 'badge',
  },
]

type TooltipContentProps = {
  lines: readonly string[]
}

const TooltipContent = ({ lines }: TooltipContentProps) => {
  if (lines.length === 1) {
    return <T.BodySmall>{lines[0]}</T.BodySmall>
  }

  return (
    <VStack gap={1}>
      <T.BodySmall className="font-medium">{lines[1]}</T.BodySmall>
      <T.BodySmall className="text-neutral-600">{lines[0]}</T.BodySmall>
    </VStack>
  )
}

const renderTrigger = ({ ariaLabel, display, role, variant }: LanguageSupportEntry) => {
  if (variant === 'badge') {
    return (
      <div
        aria-label={ariaLabel}
        className={`inline-flex items-center rounded px-2 py-1 text-xs font-medium text-neutral-700 transition-colors hover:bg-neutral-50 ${hoverFocusRingClassName}`}
        role={role}
        tabIndex={0}
      >
        <div aria-hidden="true">
          {display} <Icon.FaLanguage />
        </div>
      </div>
    )
  }

  return (
    <div
      aria-label={ariaLabel}
      className={`inline-flex items-center rounded p-1 text-xl transition-colors hover:bg-neutral-50 ${hoverFocusRingClassName}`}
      role={role}
      tabIndex={0}
    >
      <div aria-hidden="true">{display}</div>
    </div>
  )
}

const AIChatLanguageSupportRow = () => {
  return (
    <HStack align="center" gap={2} wrap="wrap" width="auto">
      {languageSupportEntries.map((entry) => (
        <Tooltip
          key={entry.id}
          content={<TooltipContent lines={entry.tooltipLines} />}
          trigger="hover"
        >
          {renderTrigger(entry)}
        </Tooltip>
      ))}
    </HStack>
  )
}

export default React.memo(AIChatLanguageSupportRow)
